import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { BodyText, Button, Collapsible, Icons } from "@/components/ui";
import { Separator } from "@/components/ui";
import { formatDateTime } from "@/lib/time";
import { hasMarkdownFormatting } from "@/lib/utils";
import { useState } from "react";
import { type Addendum, AddendumStatus } from "./types";

interface SubjectiveAddendumProps {
  summary: string;
  author?: string;
  time?: string;
  status?: AddendumStatus;
  addendumId: string;
  onAddAddendum: (addendum: Addendum) => void;
  addedAddendums: Addendum[];
  canWriteSubjective: boolean;
}

export const SubjectiveAddendum = ({
  summary,
  author,
  time,
  status,
  addendumId,
  onAddAddendum,
  addedAddendums,
  canWriteSubjective,
}: SubjectiveAddendumProps) => {
  const [open, setOpen] = useState(false);

  // Derived from props — no local state needed. Parent owns addedAddendums,
  // so on click the parent's setState triggers a re-render and isAdded
  // recomputes naturally.
  const isAdded =
    status === AddendumStatus.ACCEPTED ||
    addedAddendums.some((a) => a.id === addendumId);

  const handleAddToSubjective = () => {
    const timestamp = formatDateTime(time);
    const authorPrefix = author ? `${author} update` : "update";
    const prefixedText = `\n**[${authorPrefix} ${timestamp}]**\n\n${summary}`;
    onAddAddendum({ id: addendumId, text: prefixedText });
    setOpen(false);
  };

  const handleHide = () => {
    setOpen(false);
  };

  const getTitle = () => {
    const authorPrefix = author ? `${author} Update` : "Update";

    if (!summary) return authorPrefix;

    const words = summary.split(" ");
    // Temp title until we get one from Nori
    const shortTitle = words.slice(0, 3).join(" ");
    const needsEllipsis = words.length >= 4;

    return `${authorPrefix} - ${shortTitle}${needsEllipsis ? "..." : ""}`;
  };

  return (
    <>
      <div className="p-3">
        <Collapsible
          title={getTitle()}
          open={open}
          controlled={true}
          headerSize="sm"
          headerTextColor="superclinical"
          border="none"
          status={formatDateTime(time)}
          statusBadge={
            isAdded ? (
              <Icons.Check className="h-3 w-3 text-tertiary" />
            ) : undefined
          }
          background={open ? "superclinical" : "default"}
          className={open ? "p-2" : ""}
          onOpenChange={(open) => {
            setOpen(open);
          }}
        >
          <div className="flex flex-col mb-2">
            {hasMarkdownFormatting(summary) ? (
              <MarkdownRenderer content={summary} />
            ) : (
              <BodyText size="sm" preserveLineBreaks>
                {summary}
              </BodyText>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={handleHide}
              variant="secondary"
              size="xs"
              className="border-ai text-superclinical"
              leftIcon={Icons.EyeOff}
            >
              Hide
            </Button>
            {isAdded ? (
              <Button
                onClick={handleAddToSubjective}
                size="xs"
                variant="secondary"
                className="border-ai text-superclinical"
                leftIcon={Icons.RotateCcw}
                disabled={!canWriteSubjective}
              >
                Re-add to Subjective
              </Button>
            ) : (
              <Button
                onClick={handleAddToSubjective}
                size="xs"
                className="border-ai bg-button-superclinical text-white"
                leftIcon={Icons.ArrowUp}
                disabled={!canWriteSubjective}
              >
                Add to Subjective
              </Button>
            )}
          </div>
        </Collapsible>
      </div>
      <Separator />
    </>
  );
};
